import { formatThousandAndUnit, keepOneNull, keepOneOrLine, keepOneAndWan, keepWithNumberOrNull } from '@/utils/utils';
import dayjs from 'dayjs';
import * as echarts from 'echarts';
import { isNil } from 'lodash-es';
import { normalizeFieldSeriesUnit } from '@/pages/WgqAllMaps/utils';

type TimeActive = 'M' | 'Y';

export const getLineChartOption = (data: any[] = [], activeKey: TimeActive) => {
  const totalFeeIsAllOverMillion = data
    .filter(v => typeof v.totalFee === 'number')
    .every(m => (m.totalFee ?? 0) > 10000);
  const avgPriceIsAllOverMillion = data
    .filter(v => typeof v.avgPrice === 'number')
    .every(m => (m.avgPrice ?? 0) > 10000);

  const timeFormat = activeKey === 'M' ? 'MM-DD' : 'M月';
  const legend = {
    right: activeKey === 'M' ? 8 : 40,
    top: 8,
    bottom: 8,
    icon: 'rect',
    textStyle: {
      color: 'rgba(255,255,255,0.85)',
    },
    itemWidth: 12,
    itemHeight: 12,
    itemGap: 24,
    padding: [0, 8, 0, 0],
  };

  const areaColors = [
    new echarts.graphic.LinearGradient(0, 0, 0, 1, [
      { offset: 0, color: '#16DD8E20' },
      { offset: 1, color: '#16DD8E10' },
    ]),
    new echarts.graphic.LinearGradient(0, 0, 0, 1, [
      { offset: 0, color: '#FFCB4720' },
      { offset: 1, color: '#FFCB4710' },
    ]),
  ];

  const yAxisRender =
    activeKey === 'Y'
      ? [
          {
            type: 'value',
            name: totalFeeIsAllOverMillion ? '万元' : '元',
            position: 'left',
          },
          {
            type: 'value',
            name: avgPriceIsAllOverMillion ? '万元' : '元',
            position: 'right',
            axisLabel: {
              formatter: (v: number) => Number(v.toFixed(2)),
            },
            alignTicks: true,
          },
        ]
      : [
          {
            type: 'value',
            name: totalFeeIsAllOverMillion ? '万元' : '元',
            position: 'left',
          },
        ];

  const seriesRender = [
    ...(activeKey === 'Y'
      ? [
          {
            name: '总电量电费',
            type: 'line',
            data: data.map(m => [
              m.tsStr,
              totalFeeIsAllOverMillion ? formatThousandAndUnit(m.totalFee, 1).value : keepWithNumberOrNull(m.totalFee),
            ]),
            smooth: true,
            yAxisIndex: 0,
            areaStyle: {
              color: areaColors[0],
            },
          },
          {
            name: '每度电均价',
            type: 'line',
            data: data.map(m => [
              m.tsStr,
              avgPriceIsAllOverMillion
                ? formatThousandAndUnit(m.avgPrice, 1).value
                : keepWithNumberOrNull(m.avgPrice, 2),
            ]),
            smooth: true,
            yAxisIndex: 1,
            areaStyle: {
              color: areaColors[1],
            },
          },
        ]
      : [
          {
            name: '总电量电费',
            type: 'line',
            data: data.map(m => [
              m.tsStr,
              totalFeeIsAllOverMillion ? formatThousandAndUnit(m.totalFee, 1).value : keepWithNumberOrNull(m.totalFee),
            ]),
            smooth: true,
            yAxisIndex: 0,
            areaStyle: {
              color: areaColors[0],
            },
          },
        ]),
  ];
  return {
    backgroundColor: 'transparent',
    color: ['#16DD8E', '#FFCB47'],
    legend: legend,
    tooltip: {
      formatter: (items: any) => {
        let { axisValue } = items[0];
        let firstStr = dayjs(axisValue, 'YYYY-MM-DD').format(timeFormat);
        let otherStr = '';
        let str = '';

        items.forEach((item: any) => {
          const { seriesName, data, marker, seriesIndex } = item;
          otherStr += `<div style="margin-top:8px;width:200px;"><div style="display:flex;justify-content:space-between;align-items:center;"><div><span>${marker}</span>${seriesName}</div><div>${
            !isNil(data[1]) ? data[1] : '-'
          }${
            seriesIndex === 0 ? (totalFeeIsAllOverMillion ? '万元' : '元') : avgPriceIsAllOverMillion ? '万元' : '元'
          }</div></div>`;
          str = firstStr + otherStr;
        });

        return str;
      },
    },
    grid: {
      left: 20,
      right: 10,
      top: 40,
      bottom: 10,
      containLabel: true,
    },
    xAxis: [
      {
        type: 'category',
        axisLabel: {
          formatter: function (e: number) {
            return dayjs(e, 'YYYY-MM-DD').format(timeFormat);
          },
          showMinLabel: true,
          showMaxLabel: true,
          hideOverlap: true,
        },
        boundaryGap: true,
      },
    ],
    yAxis: yAxisRender,
    series: seriesRender,
  };
};

export const getBarChartOption = (data: any[] = [], activeKey: TimeActive) => {
  const timeFormat = activeKey === 'M' ? 'MM-DD' : 'M月';

  const { normalizedData, unit } = normalizeFieldSeriesUnit(data, {
    valueKeys: ['summit', 'peak', 'plain', 'valley'],
    baseUnit: 'kWh',
  });

  return {
    backgroundColor: 'transparent',
    tooltip: {
      formatter: (items: any) => {
        let { axisValue } = items[0];
        let firstStr = dayjs(axisValue, 'YYYY-MM-DD').format(timeFormat);
        let otherStr = '';
        let str = '';
        items.forEach((item: any) => {
          const { seriesName, data, marker } = item;
          const value = data[1];
          otherStr += `<div style="margin-top:8px;width:200px;"><div style="display:flex;justify-content:space-between;align-items:center;"><div><span>${marker}</span>${seriesName}</div><div>${value}${unit}</div></div>`;
          str = firstStr + otherStr;
        });

        return str;
      },
    },
    legend: {
      show: true,
      right: 10,
      itemHeight: 8,
      itemWidth: 8,
      data: ['尖电量', '峰电量', '平电量', '谷电量'],
      icon: 'rect',
      itemGap: 10,
    },
    grid: {
      left: 20,
      right: 20,
      top: 28,
      bottom: 10,
      containLabel: true,
    },
    xAxis: [
      {
        type: 'category',
        axisLabel: {
          formatter: function (e: string) {
            return dayjs(e, 'YYYY-MM-DD').format(timeFormat);
          },
          hideOverlap: true,
          showMinLabel: true,
          showMaxLabel: true,
        },
        boundaryGap: true,
      },
    ],
    yAxis: [
      {
        type: 'value',
        name: unit,
      },
    ],
    series: [
      {
        type: 'bar',
        stack: 'total',
        color: '#63BC7F',
        name: '谷电量',
        barMaxWidth: 20,
        data: normalizedData.map(a => [a.tsStr, keepOneNull(a.valley)]),
      },
      {
        type: 'bar',
        stack: 'total',
        color: '#0DB6D9',
        name: '平电量',
        barMaxWidth: 20,
        data: normalizedData.map(a => [a.tsStr, keepOneNull(a.plain)]),
      },
      {
        type: 'bar',
        stack: 'total',
        color: '#CE90D1',
        name: '峰电量',
        barMaxWidth: 20,
        data: normalizedData.map(a => [a.tsStr, keepOneNull(a.peak)]),
      },
      {
        type: 'bar',
        stack: 'total',
        color: '#FF6D2C',
        barMaxWidth: 20,
        name: '尖电量',
        data: normalizedData.map(a => [a.tsStr, keepOneNull(a.summit)]),
      },
    ],
  };
};

export const getPieChartOption = (chartData?: any) => {
  const finalChartData = [
    {
      name: '尖电量',
      value: chartData?.summit,
    },
    {
      name: '峰电量',
      value: chartData?.peak,
    },
    {
      name: '平电量',
      value: chartData?.plain,
    },
    {
      name: '谷电量',
      value: chartData?.valley,
    },
  ];
  return {
    color: ['#FF6D2C', '#CE90D1', '#0DB6D9', '#63BC7F'],
    title: {
      text: '用电总量(kWh)',
      textStyle: {
        fontSize: 12,
        color: 'rgba(255,255,255,0.65)',
      },
      subtext: `{normal|${keepOneAndWan(chartData?.total).value}}{small|${keepOneAndWan(chartData?.total).unit}}`,
      subtextStyle: {
        rich: {
          normal: {
            fontSize: 20,
            color: '#FFF',
            padding: [0, 4, 0, 0],
          },
          small: {
            fontSize: 16,
            color: '#FFF',
          },
        },
      },
      left: 94,
      top: 78,
      itemGap: 8,
      textAlign: 'center',
    },
    series: [
      {
        type: 'pie',
        name: '电量(kW)',
        percentPrecision: 5,
        minAngle: 2,
        padAngle: 1,
        radius: [66, 78],
        left: 35,
        top: 35,
        width: 130,
        height: 130,
        label: {
          show: false,
        },
        data: (finalChartData ?? []).map(item => ({
          name: item.name,
          value: keepOneOrLine(item.value),
        })),
        showEmptyCircle: false,
      },
      {
        showEmptyCircle: false,
        data: (finalChartData ?? [])?.map(item => ({
          name: item.name,
          value: keepOneOrLine(item.value),
        })),
        type: 'pie',
        percentPrecision: 5,
        padAngle: 1,
        minAngle: 2,
        name: '电量(kW)',
        radius: [76, 92],
        left: 38,
        top: 38,
        width: 124,
        height: 124,
        label: {
          show: false,
        },
        itemStyle: {
          opacity: 0.2,
        },
      },
    ],
  };
};
